import { http, HttpResponse } from 'msw';

import { env } from '@/config/env';
import {
  GRADE_TO_NAPLAN_IDS_MAPPING,
  GRADE_TO_SCHOOL_CURRICULUM_IDS_MAPPING,
  IMO_GRADE_TO_VARIANT_MAPPING,
} from '@/features/user-notifications/utils/constants';
import { axios } from '@/lib/axios';
import { networkDelay } from '@/test/mocks/utils';

export interface EnrollToCoursePayload {
  grade: string;
  variant_id: string;
  email_id: string;
  batch_id?: string;
  profile_id?: string;
}

export interface EnrollToCourseResponse {
  status: 'success' | 'error';
  message: string;
}

/**
 * Helper function to get variant ID for a given grade
 */
export const getVariantIdForGrade = (grade: string) => {
  const variant_id: string =
    GRADE_TO_SCHOOL_CURRICULUM_IDS_MAPPING[
      grade as keyof typeof GRADE_TO_SCHOOL_CURRICULUM_IDS_MAPPING
    ]?.variantId;
  return variant_id;
};

/**
 * Enroll to a course from the student dashboard
 */
export const enrollToCourse = async (
  payload: EnrollToCoursePayload
): Promise<EnrollToCourseResponse> => {
  return axios.post<EnrollToCourseResponse>(
    '/user/v1/enrol_to_course_from_student_dashboard',
    payload
  );
};

/**
 * Function to enroll to school curriculum course for selected grade and grade + 1
 * This is the EXACT logic from the reference implementation
 */
export const queryFunctionEnrollToCourse = (body: {
  grade: string;
  email_id: string;
}) => {
  if (body?.grade) {
    const variant_id: string = getVariantIdForGrade(body.grade);
    const requestBody: EnrollToCoursePayload = {
      grade: body.grade,
      variant_id,
      email_id: body.email_id,
    };

    return enrollToCourse(requestBody).then(response => {
      // Enroll for grade + 1
      const gradeNum = Number.parseInt(body.grade);
      const gradesPossible = Object.keys(
        GRADE_TO_SCHOOL_CURRICULUM_IDS_MAPPING
      );
      const lastPossibleGrade = Number.parseInt(gradesPossible.at(-1) ?? '9');

      if (gradeNum < lastPossibleGrade) {
        requestBody.grade = (Number.parseInt(body.grade) + 1).toString();
        requestBody.variant_id = getVariantIdForGrade(requestBody.grade);
        return enrollToCourse(requestBody);
      }

      // Else return the first response
      return response;
    });
  }

  return Promise.resolve();
};

/**
 * Helper function to get NAPLAN variant ID for a given grade
 */
export const getNaplanVariantIdForGrade = (grade: string) => {
  const variant_id: string =
    GRADE_TO_NAPLAN_IDS_MAPPING[
      grade as keyof typeof GRADE_TO_NAPLAN_IDS_MAPPING
    ]?.variantId;
  return variant_id;
};

/**
 * Function to enroll to NAPLAN course for selected grade
 * Updated to support batch_id and profile_id as per reference implementation
 */
export const queryFunctionEnrollToNaplan = (body: {
  grade: string;
  email_id: string;
  batch_id?: string;
  profile_id?: string;
  variant_id?: string;
}) => {
  if (body?.grade) {
    const variant_id: string =
      body.variant_id ?? getNaplanVariantIdForGrade(body.grade);
    const requestBody: EnrollToCoursePayload = {
      grade:
        GRADE_TO_NAPLAN_IDS_MAPPING[
          body.grade as keyof typeof GRADE_TO_NAPLAN_IDS_MAPPING
        ]?.gradeName,
      variant_id,
      email_id: body.email_id,
      batch_id: body.batch_id,
      profile_id: body.profile_id,
    };

    return enrollToCourse(requestBody);
  }

  return Promise.resolve();
};

/**
 * Function to enroll to IMO course for selected grade
 * IMO enrollment uses variant_id from grade mapping
 */
export const queryFunctionEnrollToIMO = (body: {
  grade: string;
  email_id: string;
  profile_id?: string;
}) => {
  if (body?.grade) {
    const variant_id =
      IMO_GRADE_TO_VARIANT_MAPPING[
        body.grade as keyof typeof IMO_GRADE_TO_VARIANT_MAPPING
      ];
    const requestBody: EnrollToCoursePayload = {
      grade: body.grade,
      variant_id,
      email_id: body.email_id,
      profile_id: body.profile_id,
    };

    return enrollToCourse(requestBody);
  }

  return Promise.resolve();
};

/**
 * MSW handler for enrolling to a course from the student dashboard
 */
export const enrollToCourseMockHandler = http.post(
  `${env.API_URL}/user/v1/enrol_to_course_from_student_dashboard`,
  async ({ request }) => {
    await networkDelay(); // Realistic network delay

    const body = (await request.json()) as EnrollToCoursePayload;

    // Validate required fields
    if (!body.grade || !body.variant_id || !body.email_id) {
      return HttpResponse.json(
        {
          status: 'error',
          message: 'Missing required fields: grade, variant_id, or email_id',
        },
        { status: 400 }
      );
    }

    // Simulate successful enrollment
    const response: EnrollToCourseResponse = {
      status: 'success',
      message: `Successfully enrolled to grade ${body.grade} course`,
    };

    return HttpResponse.json(response, { status: 200 });
  }
);
